import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Edit } from "lucide-react";
import * as z from "zod";
import { updateClientClientsClientIdPut } from "@/client-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";

const editClientSchema = z.object({
  client_code: z.string().min(1, "Client code is required").max(10, "Client code must be at most 10 characters"),
  client_name: z.string().min(1, "Client name is required").max(255, "Client name must be at most 255 characters"),
});

type EditClientFormValues = z.infer<typeof editClientSchema>;

interface EditClientDialogProps {
  clientId: number;
  clientCode: string;
  clientName: string;
}

export function EditClientDialog({ clientId, clientCode, clientName }: EditClientDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<EditClientFormValues>({
    resolver: zodResolver(editClientSchema),
    defaultValues: {
      client_code: clientCode,
      client_name: clientName,
    },
  });

  const { mutateAsync: updateClient, isPending } = useMutation({
    mutationFn: updateClientClientsClientIdPut,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast({
        title: t("Success"),
        description: t("Client updated successfully"),
      });
      setOpen(false);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      let errorMessage = "Failed to update client. Please try again later.";

      if (error && typeof error === "object" && "body" in error && "detail" in error.body) {
        errorMessage = error.body.detail;
      }

      toast({
        title: t("Error"),
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (data: EditClientFormValues) => {
    await updateClient({
      clientId,
      requestBody: data,
    });
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      form.reset({
        client_code: clientCode,
        client_name: clientName,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex gap-2">
          <Edit className="h-4 w-4" />
          {t("Edit")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("Edit Client")}</DialogTitle>
          <DialogDescription>
            {t("Update the client details. Both client code and name are required.")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="client_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Client Code")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("Enter client code (max 10 characters)")} {...field} maxLength={10} />
                  </FormControl>
                  <FormMessage />
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "The client code is used to manage access to files and is used for authorization. Please be careful when modifying this value.",
                    )}
                  </p>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="client_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Client Name")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("Enter client name")} {...field} maxLength={255} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
                {t("Cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t("Updating...") : t("Update Client")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
